"use client";

import type { ImpactPartnerProfessor } from "@mw/shared/payload-types";

import { Button } from "@ui/components/core/Button";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { Link } from "@ui/components/core/Link";
import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import IconMagnifyingGlass2 from "@ui/central-icons/icons/IconMagnifyingGlass2";
import { runAfterNextBrowserPaint } from "@utilities/afterNextPaint";
import { getUrlDisplayName } from "@utilities/url";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import { useDebounceValue } from "usehooks-ts";
import ImpactDetailPopupLayout from "@/app/_blocks/BlockImpactPartnerGrid/ImpactDetailPopupLayout";

interface ImpactPartnerProfessorPopupProps {
  professors: ImpactPartnerProfessor[];
  selectedProfessor: ImpactPartnerProfessor | null;
  setSelectedProfessor: (professor: ImpactPartnerProfessor | null) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function useProfessorSearch(professors: ImpactPartnerProfessor[]) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery] = useDebounceValue(searchQuery, 300);

  const filteredProfessors = useMemo(() => {
    if (!debouncedQuery.trim()) return professors;
    const q = debouncedQuery.toLowerCase();
    return professors.filter(p => p.name.toLowerCase().includes(q) || p.university.toLowerCase().includes(q));
  }, [professors, debouncedQuery]);

  const setSearchQueryHandler = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  return { searchQuery, setSearchQuery: setSearchQueryHandler, filteredProfessors };
}

export default function ImpactPartnerProfessorPopup({
  professors,
  selectedProfessor: activeSelectedProfessor,
  setSelectedProfessor,
  open,
  onOpenChange,
}: ImpactPartnerProfessorPopupProps) {
  const t = useTranslations("ClientKeys.ImpactPartnerProfessorsList");
  const isMobile = useIsMobile();
  const selectedProfessor =
    !isMobile && !activeSelectedProfessor && professors.length > 0 ? professors[0] : activeSelectedProfessor;

  const { searchQuery, setSearchQuery, filteredProfessors } = useProfessorSearch(professors);

  useEffect(() => {
    if (open && selectedProfessor && !isMobile) {
      runAfterNextBrowserPaint(() => {
        const element = document.getElementById(selectedProfessor.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    }
  }, [selectedProfessor, open, isMobile]);

  const leftPanel = (
    <>
      <div className='tw-relative tw-shrink-0 tw-px-5 tw-pb-2 tw-pt-8'>
        <div className='tw-flex tw-items-center tw-gap-1 tw-rounded-lg tw-px-4'>
          <IconMagnifyingGlass2 className='tw-size-4 tw-text-black tw-opacity-60' aria-hidden />
          <input
            className='tw-type-xs tw-flex-1 tw-bg-cream tw-py-4 tw-px-3 tw-text-black tw-outline-none'
            placeholder={t("search_professors")}
            type='text'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <DottedBorder side='bottom' />
      </div>
      <div className='tw-min-h-0 tw-flex-1 tw-overflow-y-auto'>
        <div className='tw-relative'>
          <div className='tw-space-y-1 tw-px-5 tw-pb-4 tw-pt-4'>
            {filteredProfessors.length === 0 ? (
              <p className='tw-type-sm tw-py-4 tw-px-3 tw-text-black tw-opacity-60'>{t("no_professors_found")}</p>
            ) : (
              filteredProfessors.map(professor => (
                <Button
                  id={professor.id}
                  key={professor.id}
                  variant='elevated'
                  expand
                  onClick={() => setSelectedProfessor(professor)}
                  aria-label={professor.name}
                  aria-current={selectedProfessor?.id === professor.id ? "true" : undefined}
                  className={clsx(
                    "tw-h-auto tw-min-h-0 tw-flex-col tw-items-start tw-gap-1 tw-rounded-lg tw-py-4 tw-px-3 tw-text-left tw-justify-center tw-normal-case",
                    selectedProfessor?.id !== professor.id && "tw-bg-transparent tw-shadow-none hover:tw-bg-black/5",
                  )}
                >
                  <span className='tw-type-sm tw-text-black'>{professor.name}</span>
                </Button>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );

  const rightPanel = selectedProfessor ? (
    <div className='tw-flex tw-flex-col tw-p-5 md:tw-p-10'>
      <span className='tw-type-3xl tw-py-1 tw-mb-3 tw-text-left tw-text-black'>{selectedProfessor.name}</span>
      <p className='tw-type-base tw-py-1 tw-mb-9 tw-text-black'>{selectedProfessor.bio}</p>
      <AnimatePresence mode='wait'>
        <m.div
          key={selectedProfessor.id}
          className='tw-mb-4 tw-space-y-0 lg:tw-mb-12'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {selectedProfessor.university && (
            <div className='tw-relative tw-flex tw-justify-between tw-py-5'>
              <DottedBorder side='top' />
              <p className='tw-flex-1 tw-type-base tw-text-black tw-opacity-60'>{t("info_university")}</p>
              <p className='tw-flex-1 tw-type-base tw-text-right tw-text-black'>{selectedProfessor.university}</p>
            </div>
          )}
          {selectedProfessor.website && (
            <div className='tw-relative tw-flex tw-justify-between tw-py-5'>
              <DottedBorder side='top' />
              <p className='tw-flex-1 tw-type-base tw-text-black tw-opacity-60'>{t("info_website")}</p>
              <Link
                href={selectedProfessor.website}
                className='tw-flex tw-flex-1 tw-justify-end tw-type-base tw-p-0 tw-py-0 tw-leading-none'
                rel='noopener noreferrer'
                target='_blank'
                variant='clean'
                style={{ color: "#4285F4" }}
              >
                {getUrlDisplayName(selectedProfessor.website)}
              </Link>
            </div>
          )}
          {selectedProfessor.useCase && (
            <div className='tw-relative tw-flex tw-justify-between tw-py-5'>
              <DottedBorder side='top' />
              <p className='tw-flex-1 tw-type-base tw-text-black tw-opacity-60'>{t("info_use_case")}</p>
              <p className='tw-flex-1 tw-type-base tw-text-right tw-text-black'>{selectedProfessor.useCase}</p>
            </div>
          )}
        </m.div>
      </AnimatePresence>
    </div>
  ) : null;

  return (
    <ImpactDetailPopupLayout
      open={open}
      onOpenChange={onOpenChange}
      dialogTitle={selectedProfessor?.name ?? ""}
      hasSelection={!!selectedProfessor}
      leftPanel={leftPanel}
      rightPanel={rightPanel}
      onBack={() => setSelectedProfessor(null)}
      backButtonLabel={t("back_to_list")}
    />
  );
}
